import { size } from 'lodash';
import ChunkBuffer from './ChunkBuffer';
import {
    asrConnectionClosed,
    asrVendorConfigError,
    recognitionReady,
    updateMicAsrResults,
    updateMicAsrSpeechPace,
    updateMicAsrVoiceMetrics,
    updateMicPhraseSpottingResults,
    updateSessionDurationMsec,
    updateSpeakerAsrResults,
    updateSpeakerAsrVoiceMetrics
} from '../store/actions/backendAsr';
import { recLogger } from '../utils/logger/loggerFactory';
import { proto as vibeProto } from '../proto/AsrProto';
import AsrConnection from './asr/AsrConnection';
import {
    AUDIO_INPUT_DEVICE_KEY,
    CALL_NOTIFICATION_DURATION,
    CALL_NOTIFICATION_END_PHRASE,
    CALL_NOTIFICATION_RESTART_PHRASE,
    CALL_NOTIFICATION_START_PHRASE,
    CALL_NOTIFICATION_TIMOUT,
    CALL_TIMER_UPDATE_INTERVAL,
    CallMode,
    CHANNEL,
    DEFAULT_CHUNK_BUFFER,
    EXPLICIT_INTEGRATION_SOURCE,
    EXPLICIT_MODE_RELOAD_TIMEOUT,
    KEYPHRASE_DETECTION_SILENCE_TIMOUT,
    KEYPHRASE_GROUP_NAME_FOR_START,
    KEYPHRASE_GROUP_NAME_FOR_STOP,
    RECORDER_ERROR_RELOAD_TIMEOUT,
    wsConnectionClosingCodes
} from '../constants/backend';
import {
    callDetectionStarted,
    callDetectionStopped,
    callEndReceived,
    callFeedingStarted,
    callHolded,
    callNotification,
    callStartReceived,
    callUnholded,
    resetSessionDuration,
    types as callTypes,
    utcPingTimeout
} from '../store/actions/backendCall';
import { guid } from '../utils/background/Utils';
import { checkForBigerThanInt32 } from '../utils/background/general';
import { IS_ELECTRON_APP_WITH_RECORDING, IS_LEGACY_LISTENER_MODE, IS_UI_ONLY_MODE } from '../config/electron';
import { audioChunkReceived } from '../store/actions/backendDevice';
import { proto as listenerProto } from '../proto/ListenerProto';
import { micDeviceNotAvailable, reload } from '../store/actions/backendCommon';
import { captureException, captureInfoMessage, captureWarnMessage } from '../tracker/raven';
import type { AuthSystemInfo, AuthUserData } from '../store/types';
import type { CallEventMeta } from '../store/actions/backendCall';

const recLog = recLogger;

// Voice-energy detection (CTP-706): a standalone, backend-flag-gated feature (independent of keyphrase).
// A separate metrics-only recorder meters the CLIENT (system/output) channel and we log voice-activity
// START and END. The client audio is never sent to Vibe nor saved — the metrics-only recorder emits
// numeric loudness only, never audio. This only logs; it does not start/stop calls.
//
// loudness_lufs is a dBFS-like scale (~ -96 silence .. ~0 peak). A single fixed threshold does not
// generalise (idle level differs per machine/device), so detection is adaptive: a tracked noise floor
// plus a relative margin with hysteresis (see `_trackAcdVoiceEnergy`). These params are a starting
// point and will be calibrated from the collected logs.
const ACD_VOICE_ENERGY_ONSET_MARGIN_DB = 12; // dB above the noise floor to declare onset
const ACD_VOICE_ENERGY_RELEASE_MARGIN_DB = 6; // dB above the floor to drop back to silence (hysteresis)
const ACD_NOISE_FLOOR_LEAK_UP_DB = 0.5; // how fast the floor may rise per sample (~1s) while quiet
const ACD_VOICE_ENERGY_ABS_SILENCE_LUFS = -75; // never treat anything below this as voice
// If no SYSTEM metric frame arrives for this long while the client is "speaking", treat it as the audio
// having stopped and emit an offset (reason: metrics_gap). WASAPI loopback can simply STARVE (deliver no
// frames) when the output goes silent, so the threshold path may never see a low-loudness sample — this
// timeout makes the offset/"audio stopped" signal reliable regardless, and prototypes end-detection.
const ACD_SYSTEM_SILENCE_TIMEOUT_MS = 3000;

/**
 * Safety net for `_restartConnection`: if the new connection never emits `started`
 * (handshake error / server rejected), the promise would otherwise hang forever and
 * the queued `emitCallStartEvent` would never fire — effectively losing the call.
 */
const RESTART_CONNECTION_TIMEOUT_MSEC = 10 * 1000;
/**
 * Minimal forward step applied when the per-Vibe-session emission floor has to bump a
 * timestamp to keep it strictly increasing. Matches the recorder frame cadence (~240ms) used
 * as the default continuity step, so a bumped frame stays roughly frame-aligned for the ASR.
 * Used only as a fallback before any real per-chunk step has been observed.
 */
const VIBE_EMISSION_MIN_STEP_MSEC = 240;
/**
 * How many times we try to respawn a recorder that exited unexpectedly (device change,
 * audio-callback starvation, …) before giving up and falling back to a full page reload.
 */
const MAX_RECORDER_RESTART_ATTEMPTS = 2;
/**
 * After an in-place recorder restart we wait this long for audio to resume. If no recorder
 * chunk arrives within the window we fall back to the legacy RECORDER_EXITED reload.
 */
const RECORDER_RESTART_RECOVERY_TIMEOUT_MSEC = 5 * 1000;
// Grace window before a recorder stop is allowed to tear down the active call (CALL_END) or reload.
// A stop event is only a SIGNAL — NOT proof the current recorder is gone. The desktop delivers stop
// events from EVERY recorder process on a single channel, with no instance id surfaced to the
// renderer, so during a stop→respawn race the tail `close` of a SUPERSEDED recorder is
// indistinguishable from the live one dying. Acting on it immediately kills a call whose recorder is
// actually fine (the "call drops after ~4s" reports). Instead we defer the destructive reaction by
// this window and only act if NO fresh recorder audio arrived meanwhile — i.e. no live recorder is
// producing chunks. Comfortably above the ~240ms chunk cadence (≥8 chunks expected), far below a
// human-noticeable drop. This is the renderer-only guard; it needs no desktop-app change.
const RECORDER_STOP_FATAL_GRACE_MSEC = 2 * 1000;
const AUDIO_DIAGNOSTIC_CHECK_INTERVAL_MSEC = 5 * 1000;
const AUDIO_STALL_THRESHOLD_MSEC = 15 * 1000;
const AUDIO_FINAL_GAP_WARN_THRESHOLD_MSEC = 15 * 1000;
// Minimum WALL-CLOCK age of an active call before an in-call start-phrase detection is allowed to
// force a restart. On keyphrase opt-in the grace buffer — which holds the very start phrase that
// opened the call — is replayed back through the spotter, so that phrase is re-detected shortly
// after the call begins (spotter latency lands it several seconds in). The previous guard measured
// this with the outgoing stream timestamp (`_micTimestamp`), which is re-anchored/floor-bumped on a
// keyphrase restart and read as "far into the call", so every opening echo passed the guard and
// fired a spurious CALL_RESTART — chopping one call into a string of multi-second fragments and
// dropping the start-phrase audio. We gate on real elapsed time since the call started instead,
// with a window comfortably above the observed echo-restart period (a few-to-~11s in prod logs).
const MIN_ACTIVE_CALL_AGE_FOR_KEYPHRASE_RESTART_MSEC = 30 * 1000;
// Per-kind throttle for audio diagnostic warnings: log a given kind at most once per this window
// (instead of once per whole call). Keeps Sentry volume bounded while still surfacing that a
// condition PERSISTS (e.g. an ongoing emission-floor staircase or sustained recorder silence)
// rather than collapsing it to a single line. Exact multiplicity still rides in `diagnosticCounts`.
const AUDIO_DIAGNOSTIC_WARN_THROTTLE_MSEC = 5 * 1000;
const AUDIO_WEBSOCKET_BACKPRESSURE_WARN_BYTES = 2 * 1024 * 1024;

interface AsrConnectionManagerConfig {
    sampleRateHertz: number;
    enableRecorder?: boolean;
    recognitionUseSystem?: boolean;
    jwt?: string | null;
    userData?: AuthUserData;
    systemInfo?: AuthSystemInfo;
    recorderMicAudioDeviceName?: string | null;
    recorderSystemAudioDeviceName?: string | null;
    callDetection?: {
        explicitPingPeriodMsec?: number;
        explicitIntegrationSource?: number | null;
        appWhitelist?: string[] | null;
        optInGracePeriodMsec?: number;
    };
}

interface AudioChunkPayload {
    channel: number;
    offsetInAudioStreamMsec: number;
    audioChunk: Uint8Array;
}

interface RecorderTimestampContinuity {
    rawOffsetMsec: number;
    lastRawTimestampMsec: number | null;
    lastNormalizedTimestampMsec: number | null;
    lastStepMsec: number | null;
}

export default class AsrConnectionManager {
    private _dispatch: (action: unknown) => void;

    private _config: AsrConnectionManagerConfig | null;

    private _recognitionConfig: Record<string, unknown> | null;

    private _asrConnection: AsrConnection | null;

    private _drainingAsrConnections: Set<AsrConnection>;

    private _callStartedAt: number;

    private _recognitionUseSystem: boolean;

    private _callMode: number;

    private _shouldSendSystemSound: boolean;

    private _shouldSendMicSound: boolean;

    private _feedStarted: number;

    private _feedTimer: ReturnType<typeof setInterval> | 0;

    private _micTimestamp: number;

    private _sysTimestamp: number;

    private _firstSysChunkTimestamp: number | null;

    private _firstMicChunkTimestamp: number | null;

    private _speakerBuffer: ChunkBuffer;

    private _micBuffer: ChunkBuffer;

    private _forwardedEvents: unknown[];

    private _pendingMeta: unknown[];

    /**
     * Synchronous guard against duplicate `callStartReceived` dispatches.
     *
     * `isCallStarted()` relies on `_callStartedAt`, which is set by `startCall()` — but `startCall()`
     * runs from a Redux saga AFTER the first `callStartReceived` dispatch. Between dispatch and saga
     * execution, other ASR events (voice_metrics, asr_result, …) can race through `emitCallStartEvent`
     * and each dispatch their own `callStartReceived` with a fresh `guid()`, corrupting call identity
     * on the backend.
     */
    private _callStartEmitted: boolean;

    private _iamAliveInterval: ReturnType<typeof setInterval> | null;

    private _pingCrashTimer: ReturnType<typeof setTimeout> | null;

    private _explicitPingPeriodMsec: number;

    private _integrationWithKeyphrase: boolean;

    private _integrationWithRecorderEvents: boolean;

    private _enableRecorderDebugFlag: boolean;

    // Standalone, backend-gated feature (independent of keyphrase): meter the system-output (client)
    // channel and log voice-activity start/end. See `_trackAcdVoiceEnergy`.
    private _voiceEnergyDetectionEnabled = false;

    private _asrInBackgroundListening: boolean;

    private _appWhitelist: string[];

    private _recorderMicAudioDeviceName: string;

    private _recorderSystemAudioDeviceName: string;

    private _electronRecorderHooksInited: boolean;

    // Voice-energy detection: per-channel "currently active" state and adaptive noise-floor estimate
    // (LUFS) derived from the system-audio recorder loudness metrics. Used to emit voice-activity
    // start/end log signals via the heuristic in `_trackAcdVoiceEnergy`.
    private _acdChannelSpeaking: Record<number, boolean> = {};

    private _acdNoiseFloorLufs: Record<number, number> = {};

    private _acdMetricsStartedAt: number | null = null;

    private _acdLastClientOnsetAt: number | null = null;

    private _acdLastSystemOffsetAt: number | null = null;

    private _acdSystemSilenceTimer: ReturnType<typeof setTimeout> | null = null;

    private _recorderTimestampOffset: number;

    private _recorderTimestampContinuityByChannel: Record<number, RecorderTimestampContinuity>;

    /**
     * High-water mark of the last timestamp (msec, pre-RTP) actually emitted for the current
     * business call, tracked per outgoing channel. The ASR backend treats any timestamp at or below
     * what it has already accepted as a backward jump ("timestamp less than expected"), so this
     * floor guarantees strict monotonicity at the true send boundary regardless of recorder restarts
     * OR WebSocket reconnects.
     *
     * Anchored to the BUSINESS callId (the `callId` in the protobuf recognition config), NOT to the
     * WebSocket connection. Vibe correlates a call by that protobuf callId, and a single call can
     * span several connections (keyphrase restart, reconnect after a connection error, etc.) that all
     * reuse the same callId — so the high-water must survive a reconnect and only reset when the
     * business callId actually changes (or the call fully stops). Resetting per connection (`_start`)
     * would let the fresh-from-zero timestamps of a reconnect land below what Vibe already accepted
     * for that callId — the "timestamp=0 for non-first frame" / "went backward" incident.
     */
    private _maxEmittedMicTimestampMsec: number | null;

    private _maxEmittedSysTimestampMsec: number | null;

    /**
     * Last natural (non-bumped) increment actually emitted per channel — i.e. the duration of the
     * previous payload in the pipeline's unit. The emission floor bumps a non-increasing timestamp by
     * THIS, instead of a fixed step, so a corrected frame advances by a real audio duration and the
     * next frame does not OVERLAP the previous one. Vibe rejects overlap (`ts < prev + duration`), not
     * just backward jumps, so a duration-aware bump is what keeps us above `next_expected`.
     */
    private _lastEmittedMicStepMsec: number | null;

    private _lastEmittedSysStepMsec: number | null;

    /** Business callId the current timestamp state (continuity + floor + zero-base) belongs to. */
    private _vibeTimestampCallId: string | number | null;

    private _keyphraseCallTimestampBaseMsec: number | null;

    private _recorderStartRequested: boolean;

    // Monotonic id of the current recorder instance, bumped on every (re)start. A deferred recorder-
    // stop reaction captures the generation it was scheduled under and bails if a newer recorder has
    // since superseded it — so a stale stop from an old instance can never tear down a fresh one.
    private _recorderGeneration: number;

    // Pending deferred reaction to an unexpected recorder stop (see RECORDER_STOP_FATAL_GRACE_MSEC).
    private _fatalRecorderStopTimer: ReturnType<typeof setTimeout> | null;

    private _recorderRestartAttempts: number;

    private _recorderRestartWatchdogTimer: ReturnType<typeof setTimeout> | null;

    private _recorderRestartRequestedAt: number;

    private _expectedRecorderStops: number;

    private _expectedRecorderStopTimer: ReturnType<typeof setTimeout> | null;

    private _audioDiagnosticsTimer: ReturnType<typeof setInterval> | null;

    private _audioDiagnosticWarnings: Record<string, number>;

    // Total occurrences per diagnostic kind for the current call. `_audioDiagnosticWarnings` only
    // records the timestamp we last LOGGED each kind (throttled to one log per kind per
    // AUDIO_DIAGNOSTIC_WARN_THROTTLE_MSEC), so on its own it cannot tell "happened once" from
    // "happened 50 times" between logged samples — which previously hid how deep e.g. an
    // emission-floor staircase ran. These counts are surfaced in the diagnostics context.
    private _audioDiagnosticWarningCounts: Record<string, number>;

    private _recorderChunksReceived: number;

    private _recorderBytesReceived: number;

    private _recorderChunksDispatched: number;

    private _recorderChunksDroppedByGate: number;

    private _recorderDecodeFailures: number;

    private _vibeChunksSent: number;

    private _vibeBytesSent: number;

    private _vibeSendFailures: number;

    private _lastRecorderChunkReceivedAt: number;

    private _lastRecorderChunkTimestampMsec: number | null;

    private _lastRecorderChunkBytes: number;

    private _lastVibeChunkSentAt: number;

    private _lastVibeChunkTimestampMsec: number | null;

    private _lastVibeChunkBytes: number;

    private _lastVibeBufferedAmount: number;

    private _keyphraseDetectionSilenceTimout: ReturnType<typeof setTimeout> | null;

    private _keyphraseDetectionSilenceNotificationTimout: ReturnType<typeof setTimeout> | null;

    constructor({ dispatch }: { dispatch: (action: unknown) => void }) {
        this._dispatch = dispatch;

        this._config = null;
        this._recognitionConfig = null;

        this._asrConnection = null;
        this._drainingAsrConnections = new Set();
        this._callStartedAt = 0;

        this._recognitionUseSystem = true;

        this._callMode = CallMode.UNKNOWN;
        this._shouldSendSystemSound = false;
        this._shouldSendMicSound = false;
        this._feedStarted = 0;
        this._feedTimer = 0;
        this._micTimestamp = 0;
        this._sysTimestamp = 0;

        this._firstSysChunkTimestamp = null;
        this._firstMicChunkTimestamp = null;

        this._speakerBuffer = new ChunkBuffer(DEFAULT_CHUNK_BUFFER);
        this._micBuffer = new ChunkBuffer(DEFAULT_CHUNK_BUFFER);

        this._forwardedEvents = [];
        this._pendingMeta = [];
        this._callStartEmitted = false;
        this._iamAliveInterval = null;

        this._pingCrashTimer = null;
        this._explicitPingPeriodMsec = 0;

        this._integrationWithKeyphrase = false;
        this._integrationWithRecorderEvents = false;
        this._enableRecorderDebugFlag = false;
        this._voiceEnergyDetectionEnabled = false;
        this._appWhitelist = [];
        this._recorderMicAudioDeviceName = '';
        this._recorderSystemAudioDeviceName = '';
        this._electronRecorderHooksInited = false;
        this._recorderTimestampOffset = 0;
        this._recorderTimestampContinuityByChannel = {};
        this._maxEmittedMicTimestampMsec = null;
        this._maxEmittedSysTimestampMsec = null;
        this._lastEmittedMicStepMsec = null;
        this._lastEmittedSysStepMsec = null;
        this._vibeTimestampCallId = null;
        this._keyphraseCallTimestampBaseMsec = null;
        this._recorderStartRequested = false;
        this._recorderGeneration = 0;
        this._fatalRecorderStopTimer = null;
        this._recorderRestartAttempts = 0;
        this._recorderRestartWatchdogTimer = null;
        this._recorderRestartRequestedAt = 0;
        this._expectedRecorderStops = 0;
        this._expectedRecorderStopTimer = null;
        this._audioDiagnosticsTimer = null;
        this._audioDiagnosticWarnings = {};
        this._audioDiagnosticWarningCounts = {};
        this._recorderChunksReceived = 0;
        this._recorderBytesReceived = 0;
        this._recorderChunksDispatched = 0;
        this._recorderChunksDroppedByGate = 0;
        this._recorderDecodeFailures = 0;
        this._vibeChunksSent = 0;
        this._vibeBytesSent = 0;
        this._vibeSendFailures = 0;
        this._lastRecorderChunkReceivedAt = 0;
        this._lastRecorderChunkTimestampMsec = null;
        this._lastRecorderChunkBytes = 0;
        this._lastVibeChunkSentAt = 0;
        this._lastVibeChunkTimestampMsec = null;
        this._lastVibeChunkBytes = 0;
        this._lastVibeBufferedAmount = 0;
        this._keyphraseDetectionSilenceTimout = null;
        this._keyphraseDetectionSilenceNotificationTimout = null;

        /**
         * in KW call detection mode the assistant waits for the starting phrases as soon as the phrase is spoken this parameter will become false
         * @type {boolean}
         * @private
         */
        this._asrInBackgroundListening = false;
    }

    isCallStarted() {
        return !!this._callStartedAt;
    }

    _startPhraseDetected() {
        // mark that START phrase detected
        this._micBuffer.markAllUnSend();
        this._speakerBuffer.markAllUnSend();
    }

    _start() {
        const config = this._config;
        if (!config) {
            return;
        }
        const { sampleRateHertz, ...restConfig } = config;
        const connectionConfig = {
            ...restConfig,
            ...(restConfig.jwt ? { jwt: restConfig.jwt } : {})
        };

        this._callStartedAt = 0;
        this._callStartEmitted = false;

        // Safety net: if `_start()` is ever called while a previous connection is still owned
        // (e.g. re-entry through an external `start()` without a `stop()`), drain the old one
        // instead of overwriting the reference and leaking the socket + listeners.
        if (this._asrConnection) {
            const previous = this._asrConnection;
            this._drainingAsrConnections.add(previous);
            previous.stop({
                acdSessionOffsetMsec: 0,
                asrSessionOffsetMsec: 0,
                reason: vibeProto.FinalizationReason.CALL_END_NORMAL,
                code: wsConnectionClosingCodes.RESTART
            });
        }

        this._asrConnection = new AsrConnection('VibeConnection');
        const connection = this._asrConnection;
        if (!connection) {
            return;
        }
        // NOTE: the Vibe-timestamp state (recorder continuity + emission floor + zero-base) is
        // deliberately NOT reset here. Vibe correlates a call by the business callId, and a single
        // call can span several connections (this is just a new WebSocket for the same callId) — so
        // resetting per connection would drop the high-water mark and let a reconnect emit timestamps
        // below what Vibe already accepted. The reset is driven by a business-callId change in
        // `startCall` (and a full `stop`); see `_resetVibeSessionTimestampState`.
        const isActiveConnection = () => this._asrConnection === connection;

        // Per-connection one-shot diagnostic for stale ASR events that could
        // otherwise trigger a synthetic CALL_START via `this.callStarted()`.
        // We only log the FIRST stale event per connection: a drained connection
        // may keep emitting voice_metrics at high frequency, and per-event
        // logging would flood the log endpoint. One log line is enough to know
        // whether the scenario actually occurs in production and which event
        // type was the first to arrive late.
        let staleSyntheticTriggerLogged = false;
        const noteStaleSyntheticTrigger = (eventName: string) => {
            if (staleSyntheticTriggerLogged) return;
            staleSyntheticTriggerLogged = true;
            captureInfoMessage('asr stale event on draining connection (expected)', {
                type: 'asrStaleEvent',
                eventName,
                expected: true
            });
        };

        connection.on('stopped', (params: { code?: number; wasClean: boolean; closedByClient: boolean }) => {
            this._drainingAsrConnections.delete(connection);
            recLog.log(`Vibe connection stopped`, params);
            // If this was the active connection (not a draining one that got replaced), the
            // ping watchdog has no reason to keep ticking.
            if (this._asrConnection === connection) {
                this.stopIamAlive();
            }

            if (params.code !== wsConnectionClosingCodes.RESTART) {
                this._dispatch(asrConnectionClosed(params));
                this._dispatch(callDetectionStopped(params.code ?? 0));
            }

            if (!this._integrationWithKeyphrase && !this._integrationWithRecorderEvents) {
                this.stopElectronRecording();
            }
        });

        connection.on('asr_vendor_config_error', () => {
            this._dispatch(asrVendorConfigError());
        });

        if (this._asrInBackgroundListening) {
            // in this case we want to listen for start keyword
            recLog.log('Key phrase based call connection detection');

            connection.on('started', ({ id }: { id: string }) => {
                if (!isActiveConnection()) {
                    return;
                }
                this.startIamAlive();
                this.flushForwardedEvents();

                connection.sendRecognitionConfig({
                    callId: guid(),
                    ...(this._recognitionConfig || {})
                });

                // we do not record the stream that we use to recognize the start
                this.sendChannelControlRequest(CHANNEL.MICROPHONE, true, true);
                this.sendChannelControlRequest(CHANNEL.SYSTEM, true, true);

                this._dispatch(callDetectionStarted(id));
            });

            connection.on('phrase_spotting_result', (data: { whitelist?: Record<string, unknown>[] } & Record<string, unknown>) => {
                if (!isActiveConnection()) {
                    return;
                }
                const startPhraseGroup = data.whitelist?.find((p: Record<string, unknown>) => {
                    return p.groupName === KEYPHRASE_GROUP_NAME_FOR_START;
                });

                if (startPhraseGroup) {
                    recLog.log('Call start phrase detected');

                    this._startPhraseDetected();
                    this._dispatch(callNotification(CALL_NOTIFICATION_START_PHRASE));

                    this._dispatch({
                        type: callTypes.CALL_RESTART,
                        payload: {
                            eventMeta: {
                                source: 'kw_phrase',
                                phrase: startPhraseGroup.segments
                            }
                        }
                    });
                }
            });

            connection.start({
                sampleRateHertz,
                incallRecognitionSessionNumber: 0,
                ...connectionConfig
            });
        } else {
            connection.on('started', ({ id }: { id: string }) => {
                if (!isActiveConnection()) {
                    return;
                }
                this.startIamAlive();
                this.flushForwardedEvents();
                this._dispatch(callDetectionStarted(id));
            });

            if (IS_UI_ONLY_MODE) {
                connection.on('call_start', ({ callID }: { callID: string }) => {
                    if (!isActiveConnection()) {
                        return;
                    }
                    this.callStarted(callID);
                });
            }
            connection.on('call_hold', () => {
                if (!isActiveConnection()) {
                    return;
                }
                this.emitCallHeld();
            });
            connection.on('call_unhold', () => {
                if (!isActiveConnection()) {
                    return;
                }
                this.emitCallUnheld();
            });
            connection.on('voice_metrics', (data: Record<string, unknown>) => {
                if (!isActiveConnection()) {
                    noteStaleSyntheticTrigger('voice_metrics');
                    return;
                }
                this.callStarted();

                if (data.channel === CHANNEL.MICROPHONE) {
                    this._dispatch(updateMicAsrVoiceMetrics(data));

                    if (this._integrationWithKeyphrase) {
                        this._checkVoiceMetricsCall(data);
                    }
                } else {
                    this._dispatch(updateSpeakerAsrVoiceMetrics(data));
                }
            });
            connection.on('speech_pace', (data: Record<string, unknown>) => {
                if (!isActiveConnection()) {
                    noteStaleSyntheticTrigger('speech_pace');
                    return;
                }
                this.callStarted();

                this._dispatch(updateMicAsrSpeechPace(data));
            });
            connection.on('asr_result', (data: Record<string, unknown>) => {
                if (!isActiveConnection()) {
                    noteStaleSyntheticTrigger('asr_result');
                    return;
                }
                this.callStarted();

                if (data.channel === CHANNEL.MICROPHONE) {
                    this._dispatch(updateMicAsrResults(data));
                } else {
                    this._dispatch(updateSpeakerAsrResults(data));
                }
            });
            connection.on('phrase_spotting_result', (data: { whitelist?: Record<string, unknown>[] } & Record<string, unknown>) => {
                if (!isActiveConnection()) {
                    noteStaleSyntheticTrigger('phrase_spotting_result');
                    return;
                }
                this.callStarted();

                const startPhraseGroup = data.whitelist?.find((p: Record<string, unknown>) => {
                    return p.groupName === KEYPHRASE_GROUP_NAME_FOR_START;
                });
                const stopPhraseGroup = data.whitelist?.find((p: Record<string, unknown>) => {
                    return p.groupName === KEYPHRASE_GROUP_NAME_FOR_STOP;
                });

                // console.log('__PHRASE_SPOTTING_RESULT', data, {
                //     startPhraseGroup,
                //     stopPhraseGroup
                // });

                if (this._integrationWithKeyphrase && !!startPhraseGroup) {
                    // We skip the start phrase in a call we just started: the opt-in replays the grace
                    // buffer (which contains this very phrase) back through the spotter, so it is
                    // re-detected shortly after the call starts. Gate on WALL-CLOCK call age, not the
                    // outgoing stream timestamp (`_micTimestamp`) which is unreliable across a keyphrase
                    // restart — see MIN_ACTIVE_CALL_AGE_FOR_KEYPHRASE_RESTART_MSEC.
                    const activeCallAgeMsec = this._callStartedAt ? Date.now() - this._callStartedAt : 0;
                    const restartGuardMsec = Math.max(
                        this._micBuffer.getOptInGracePeriodMsec() * 2,
                        MIN_ACTIVE_CALL_AGE_FOR_KEYPHRASE_RESTART_MSEC
                    );
                    if (activeCallAgeMsec > restartGuardMsec) {
                        recLog.log('Call start phrase detected');

                        this._dispatch(callNotification(CALL_NOTIFICATION_RESTART_PHRASE));

                        this._dispatch({
                            type: callTypes.CALL_RESTART,
                            payload: {
                                eventMeta: {
                                    source: 'kw_phrase',
                                    phrase: startPhraseGroup.segments
                                }
                            }
                        });
                    }
                } else if (this._integrationWithKeyphrase && !!stopPhraseGroup) {
                    recLog.log('Call end phrase detected');
                    this._dispatch(callNotification(CALL_NOTIFICATION_END_PHRASE));

                    this._dispatch({
                        type: callTypes.CALL_END,
                        payload: {
                            eventMeta: {
                                source: 'kw_phrase',
                                phrase: stopPhraseGroup.segments
                            }
                        }
                    });
                } else {
                    this._dispatch(updateMicPhraseSpottingResults(data));
                }
            });

            connection.start({
                sampleRateHertz,
                incallRecognitionSessionNumber: 0,
                ...connectionConfig
            });
        }
    }

    callStarted(callID: string | null = null) {
        this.emitCallStartEvent({
            callID,
            sessionOffsetMsec: 0,
            eventMeta: callID ? undefined : { source: 'synthetic_asr_event' }
        });
    }

    feedingStarted() {
        if (!this._feedStarted && this.isCallStarted()) {
            this._feedStarted = Date.now();

            this._feedTimer = setInterval(() => {
                const diff = this._feedStarted > 0 ? Date.now() - this._feedStarted : 0;

                this._dispatch(updateSessionDurationMsec(diff));
            }, CALL_TIMER_UPDATE_INTERVAL);

            this._dispatch(callFeedingStarted());
        }
    }

    sendForgetAudio() {
        this.sendChannelControlRequest(CHANNEL.MICROPHONE, false, true);
        this.sendChannelControlRequest(CHANNEL.SYSTEM, false, true);
    }

    setCallMode(mode: number, sendChannelControlRequest = true, forgetAudioAndTranscript = false) {
        if (!this.isCallStarted()) {
            return;
        }

        if (this._callMode === CallMode.NONE) {
            return;
        }

        recLog.log(
            `call mode changed:: [${
                this._shouldSendSystemSound ? '_shouldSendSystemSound' : ''
            }, ${this._shouldSendMicSound ? '_shouldSendMicSound' : ''}]`
        );

        switch (mode) {
            case CallMode.BOTH:
                recLog.log(`call mode changed:: BOTH`);

                this._callMode = CallMode.BOTH;
                this._shouldSendSystemSound = true;
                this._shouldSendMicSound = true;

                this.feedingStarted();

                break;
            case CallMode.AGENT:
                recLog.log(`call mode changed:: AGENT`);

                this._callMode = CallMode.AGENT;
                this._shouldSendSystemSound = false;
                this._shouldSendMicSound = true;

                this.feedingStarted();

                if (sendChannelControlRequest && forgetAudioAndTranscript) {
                    this.sendChannelControlRequest(CHANNEL.SYSTEM); // sys
                }
                break;
            case CallMode.CUSTOMER:
                recLog.log(`call mode changed:: CUSTOMER`);

                this._callMode = CallMode.CUSTOMER;
                this._shouldSendSystemSound = true;
                this._shouldSendMicSound = false;

                this.feedingStarted();

                if (sendChannelControlRequest && forgetAudioAndTranscript) {
                    this.sendChannelControlRequest(CHANNEL.MICROPHONE); // mic
                }
                break;
            case CallMode.META:
                recLog.log(`call mode changed:: META`);

                this._callMode = CallMode.META;
                this._shouldSendSystemSound = false;
                this._shouldSendMicSound = false;

                if (sendChannelControlRequest) {
                    this.sendChannelControlRequest(CHANNEL.MICROPHONE, false); // mic
                    this.sendChannelControlRequest(CHANNEL.SYSTEM, false); // sys
                }
                break;
            case CallMode.NONE:
                recLog.log(`call mode changed:: NONE`);

                this._callMode = CallMode.NONE;
                this._shouldSendSystemSound = false;
                this._shouldSendMicSound = false;

                if (sendChannelControlRequest) {
                    this.sendChannelControlRequest(CHANNEL.MICROPHONE, true); // mic
                    this.sendChannelControlRequest(CHANNEL.SYSTEM, true); // sys
                }
                break;
            case CallMode.UNKNOWN:
                recLog.log(`call mode changed:: UNKNOWN (reset)`);

                this._callMode = CallMode.UNKNOWN;
                this._shouldSendSystemSound = false;
                this._shouldSendMicSound = false;

                break;
            default:
        }
    }

    /**
     * @param channel - AGENT = 1; CUSTOMER = 2;
     * @param forgetAllData true/false
     * @param forgetAudioOnly true/false
     */
    sendChannelControlRequest(channel: number, forgetAllData = false, forgetAudioOnly = false) {
        if (this.isConnectionOpen()) {
            const connection = this._asrConnection;
            if (!connection) {
                return;
            }
            connection.sendChannelControlRequest(channel, forgetAllData, forgetAudioOnly);
        }
    }

    /**
     *
     * @param sessionOffsetMsec // timestamp of last received audio signal
     * @param callID // uuid of call
     * @param meta some info from telephony system
     */
    emitCallStartEvent = ({
        sessionOffsetMsec,
        callID,
        meta,
        eventMeta
    }: {
        sessionOffsetMsec: number;
        callID?: string | null;
        meta?: unknown;
        eventMeta?: CallEventMeta;
    }) => {
        // Synchronous guard: we cannot rely on `isCallStarted()` because `_callStartedAt` is only
        // populated by `startCall()` which runs from a Redux saga AFTER the first dispatch. That
        // leaves a window during which racing ASR events each fire their own `callStartReceived`
        // with a fresh `guid()`. `_callStartEmitted` closes that window in the same sync tick.
        if (this._callStartEmitted || this.isCallStarted()) return;

        if (this._asrInBackgroundListening) {
            // Same guard needs to survive the async restart — otherwise a second caller during
            // `_restartConnection` could kick off a parallel restart + duplicate dispatch.
            this._callStartEmitted = true;
            // restart Vibe connection & start call
            this._restartConnection()
                .then(() => {
                    // Release the guard so the recursive call can proceed and actually dispatch.
                    this._callStartEmitted = false;
                    this.emitCallStartEvent({ sessionOffsetMsec, callID, meta, eventMeta });
                })
                .catch(err => {
                    this._callStartEmitted = false;
                    captureException(err instanceof Error ? err : new Error(String(err)));
                });
            return;
        }

        this._callStartEmitted = true;

        const id = callID || guid();
        recLog.log(`call started:: ${id}`);

        this._dispatch(
            callStartReceived({
                sessionOffsetMsec,
                id,
                metaCallID: (meta as { call_id?: string } | undefined)?.call_id || null,
                eventMeta: eventMeta || {}
            })
        );

        if (meta) {
            if (this._asrConnection) {
                this._asrConnection.sendMeta(meta);
            } else {
                // Queue meta so it survives until a connection becomes available instead of being
                // dropped to Sentry and forgotten — losing meta severs the link to telephony/CRM.
                this._pendingMeta.push(meta);
                captureException(
                    new Error(
                        JSON.stringify({
                            message: 'ASR connection missing; meta queued for next flush',
                            payload: meta
                        })
                    )
                );
            }
        }

        if (this._explicitPingPeriodMsec > 0) {
            this._pingCrashTimer = setTimeout(this.pingCrash.bind(this), this._explicitPingPeriodMsec);
        }
    };

    emitCallEndEvent = ({
        sessionOffsetMsec,
        reason,
        eventMeta
    }: {
        sessionOffsetMsec: number;
        reason?: unknown;
        eventMeta?: CallEventMeta;
    }) => {
        this._dispatch(callEndReceived({ sessionOffsetMsec, reason, eventMeta }));

        if (this._pingCrashTimer) {
            clearTimeout(this._pingCrashTimer);
            this._pingCrashTimer = null;
        }
    };

    emitCallHeld = () => this._dispatch(callHolded());

    emitCallUnheld = () => this._dispatch(callUnholded());

    start(config: AsrConnectionManagerConfig, recognitionConfig: Record<string, unknown>) {
        this._config = config;
        this._recognitionConfig = recognitionConfig;

        (window as Window & { _enableRecorder?: unknown })._enableRecorder = config.enableRecorder;

        this._recognitionUseSystem = config.recognitionUseSystem ?? true;
        this._explicitPingPeriodMsec = config.callDetection?.explicitPingPeriodMsec || 0;
        this._integrationWithKeyphrase =
            config.callDetection?.explicitIntegrationSource === EXPLICIT_INTEGRATION_SOURCE.KEYPHRASE_AUDIO_EVENT;
        this._integrationWithRecorderEvents =
            config.callDetection?.explicitIntegrationSource === EXPLICIT_INTEGRATION_SOURCE.RECORDER_EVENT;
        this._appWhitelist = config.callDetection?.appWhitelist || [];
        this._recorderMicAudioDeviceName = config.recorderMicAudioDeviceName || localStorage.getItem(AUDIO_INPUT_DEVICE_KEY) || '';
        this._recorderSystemAudioDeviceName = config.recorderSystemAudioDeviceName || '';

        this._asrInBackgroundListening = this._integrationWithKeyphrase;

        if (this._integrationWithKeyphrase || this._integrationWithRecorderEvents) {
            this.startElectronRecording();
        }

        const optInGracePeriodMsec = config.callDetection?.optInGracePeriodMsec || 0;

        this._speakerBuffer.setOptInGracePeriodMsec(optInGracePeriodMsec);
        this._micBuffer.setOptInGracePeriodMsec(optInGracePeriodMsec);

        this._start();
    }

    startCall(recognitionConfig: Record<string, unknown>) {
        // The business callId (the protobuf `callId`) decides whether this is a real new call or a
        // spurious duplicate. Vibe keys a call by that callId, and every real start — including the
        // agent repeating the greeting — carries a fresh callId (see `emitCallStartEvent`, which
        // generates a new guid per start). So:
        //   • a NEW callId  => a genuinely new call: rebase the timestamp state and (re)initialise the
        //     recorder/buffer cleanly. This is what makes a repeated start START A NEW CALL.
        //   • the SAME callId while the call is still live => a duplicate signal (event races, the
        //     keyphrase firing twice for the same call): make it idempotent — re-running the setup
        //     would restart the recorder and re-base `_firstMicChunkTimestamp` on the live stream and
        //     collapse every later timestamp below what Vibe already accepted ("timestamp=0 for
        //     non-first frame" / "went backward").
        const callId = (recognitionConfig.callId ?? (recognitionConfig.sessionInfo as { callId?: unknown } | undefined)?.callId ?? null) as
            | string
            | number
            | null;
        const isNewCall = callId !== this._vibeTimestampCallId;
        const isDuplicateOfLiveCall = this.isCallStarted() && !isNewCall;

        this._recognitionConfig = recognitionConfig;
        this._resetAudioDiagnostics();

        if (isNewCall) {
            this._resetVibeSessionTimestampState();
            this._vibeTimestampCallId = callId;
        }

        this._resetRecorderRestartRecovery();

        this.stopIamAlive();

        if (IS_LEGACY_LISTENER_MODE || IS_ELECTRON_APP_WITH_RECORDING) {
            const connection = this._asrConnection;
            if (!connection) {
                return;
            }

            connection.sendRecognitionConfig({
                ...recognitionConfig
            });
        }

        this._dispatch(recognitionReady());

        if (!isDuplicateOfLiveCall) {
            if (!this._integrationWithRecorderEvents && !this._integrationWithKeyphrase) {
                this.startElectronRecording();
            }

            if (!this._integrationWithKeyphrase) {
                // Clear any pre-call buffer; the zero-base is rebased above on a callId change.
                this.resetBuffer(false);
            }
        }

        this._callStartedAt = Date.now();
        this._startAudioDiagnosticsWatchdog();

        if (!this._asrInBackgroundListening) {
            this.feedingStarted();
        }
    }

    resetCallMode() {
        this._callMode = CallMode.UNKNOWN;
        this._shouldSendSystemSound = false;
        this._shouldSendMicSound = false;
        this._feedStarted = 0;

        localStorage.removeItem('wasOptInPressed');

        if (this._feedTimer) {
            clearInterval(this._feedTimer);
            this._feedTimer = 0;
        }

        this._clearKeyphraseSilenceTimers();
    }

    stop({
        acdSessionOffsetMsec,
        asrSessionOffsetMsec,
        reason,
        code
    }: {
        acdSessionOffsetMsec: number;
        asrSessionOffsetMsec: number;
        reason?: unknown;
        code?: number;
    }) {
        recLog.log('CALL DURATION:', asrSessionOffsetMsec);
        recLog.log('CALL END REASON:', reason);
        recLog.log('Speaker Buffer size on call end send/all:', `${this._speakerBuffer.countOfSend()}/${this._speakerBuffer.size()}`);
        recLog.log('Mic Buffer size on call end send/all:', `${this._micBuffer.countOfSend()}/${this._micBuffer.size()}`);
        this._warnFinalAudioGap(asrSessionOffsetMsec);
        this._stopAudioDiagnosticsWatchdog();

        this.resetBuffer();
        this.resetCallMode();
        this._resetVibeSessionTimestampState();
        this._resetRecorderRestartRecovery();
        this.stopIamAlive();
        if (this._pingCrashTimer) {
            clearTimeout(this._pingCrashTimer);
            this._pingCrashTimer = null;
        }

        // Drain the ASR connection regardless of its WebSocket state. Previously this branch was
        // gated by `isConnectionOpen()`, which left a connection in the CONNECTING state leaked:
        // the WS would open later and emit events against a stopped manager, producing phantom
        // call state. `AsrConnection.stop()` is idempotent and handles the not-yet-open case via
        // `_stopRequestedByClient` — it will close the socket as soon as it opens.
        const connection = this._asrConnection;
        if (connection) {
            this._forwardedEvents = [];
            this._pendingMeta = [];
            this._asrConnection = null;
            this._drainingAsrConnections.add(connection);
            connection.stop({
                acdSessionOffsetMsec: checkForBigerThanInt32(acdSessionOffsetMsec) ? 0 : acdSessionOffsetMsec,
                asrSessionOffsetMsec: checkForBigerThanInt32(asrSessionOffsetMsec) ? 0 : asrSessionOffsetMsec,
                reason: reason as number | undefined,
                code
            });
        }

        this._callStartedAt = 0;
        this._callStartEmitted = false;
    }

    async _restartConnection() {
        recLog.log('ASR Connection Restart');
        // NOTE: we deliberately DO NOT clear `_forwardedEvents` / `_pendingMeta` here. These are
        // client-side queues that must survive the connection swap — `flushForwardedEvents` will
        // drain them against the new connection once it emits `started`. Clearing on restart
        // silently loses UCT events and telephony meta and was a confirmed source of orphan
        // calls on the backend.
        if (this._asrConnection) {
            const connection = this._asrConnection;
            this._asrConnection = null;
            this._drainingAsrConnections.add(connection);
            connection.stop({
                acdSessionOffsetMsec: 0,
                asrSessionOffsetMsec: 0,
                reason: vibeProto.FinalizationReason.CALL_END_NORMAL,
                code: wsConnectionClosingCodes.RESTART
            });
        }
        this.stopIamAlive();
        this._clearKeyphraseSilenceTimers();
        recLog.log('ASR Connection Detached');
        await new Promise<void>(resolve => {
            this._asrInBackgroundListening = false;
            // Anchor the restarted recorder to the absolute tail of the background buffer so the
            // fresh recorder clock (which starts at 0) lands right after the replayed grace buffer.
            // Outgoing timestamps are rebased later, at the send boundary, so this absolute anchor
            // preserves continuity without leaking the background-listener clock into the new call.
            this.startElectronRecording(this._micBuffer.getLastOffsetMsec());
            this._start();

            const connection = this.getAsrConnection();
            if (!connection) {
                resolve();
                return;
            }

            // Fail-safe timeout: without it, a connection that never emits `started`
            // (handshake rejected, network flap) would leave the awaiter hanging and
            // prevent `emitCallStartEvent` from ever proceeding — losing the call.
            let settled = false;
            const settle = () => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                resolve();
            };

            const timer = setTimeout(() => {
                if (settled) return;
                captureException(new Error('AsrConnectionManager._restartConnection timed out waiting for started'));
                settle();
            }, RESTART_CONNECTION_TIMEOUT_MSEC);

            connection.on('started', settle);
            // Also unblock if the fresh connection closes before it ever opens — otherwise the
            // awaiter would tick all the way to the timeout for no reason.
            connection.on('stopped', settle);
        });
    }

    _checkVoiceMetricsCall(metrics: { ourSideIsSpeaking?: boolean }) {
        if (!metrics.ourSideIsSpeaking) {
            if (!this._keyphraseDetectionSilenceTimout) {
                this._keyphraseDetectionSilenceTimout = setTimeout(
                    () =>
                        this._dispatch({
                            type: callTypes.CALL_END,
                            payload: {
                                eventMeta: {
                                    source: 'silence_timeout'
                                }
                            }
                        }),
                    KEYPHRASE_DETECTION_SILENCE_TIMOUT
                );
                this._keyphraseDetectionSilenceNotificationTimout = setTimeout(
                    () => this._dispatch(callNotification(CALL_NOTIFICATION_TIMOUT)),
                    KEYPHRASE_DETECTION_SILENCE_TIMOUT - CALL_NOTIFICATION_DURATION
                );
            }
        } else {
            this._clearKeyphraseSilenceTimers();
        }
    }

    /**
     * Silence timers fire `CALL_END` / silence notification after `KEYPHRASE_DETECTION_SILENCE_TIMOUT`.
     * If a call ends or restarts before they fire, we MUST clear them — otherwise the callback runs
     * against a new call (or none at all), producing ghost `CALL_END` dispatches.
     */
    private _clearKeyphraseSilenceTimers() {
        if (this._keyphraseDetectionSilenceTimout) {
            clearTimeout(this._keyphraseDetectionSilenceTimout);
            this._keyphraseDetectionSilenceTimout = null;
        }
        if (this._keyphraseDetectionSilenceNotificationTimout) {
            clearTimeout(this._keyphraseDetectionSilenceNotificationTimout);
            this._keyphraseDetectionSilenceNotificationTimout = null;
        }
    }

    isConnectionOpen() {
        return this._asrConnection && this._asrConnection.isOpened();
    }

    private getAsrConnection(): AsrConnection | null {
        return this._asrConnection;
    }

    enqueueChunks({ audioChunk }: { audioChunk: AudioChunkPayload }) {
        if (audioChunk.channel === listenerProto.DataFlow.CAPTURE) {
            this._micBuffer.enqueue(audioChunk);
        } else {
            this._speakerBuffer.enqueue(audioChunk);
        }
    }

    timestampToRtpTimestamp(timestamp: number) {
        return Math.round(
            (window as Window & { hasOpus?: boolean }).hasOpus || IS_ELECTRON_APP_WITH_RECORDING
                ? // listner OPUS 48000 / (1000/240) = 11520
                  48 * timestamp
                : // mock WAV: 16000 / (1000/240) = 3840
                  16 * timestamp
        );
    }

    /**
     * RTP sample timestamp → pipeline milliseconds. NOTE: deliberately NOT rounded — the recorder
     * emits a sample-accurate RTP counter, and rounding samples→ms here (then ms→samples at the send
     * boundary via `timestampToRtpTimestamp`) desynchronised the value from the true sample count by a
     * few samples per chunk on variable-length Opus packets, surfacing as sub-millisecond overlaps
     * Vibe rejected ("timestamp less than expected"). Carrying fractional ms makes the round-trip
     * exact: `round(48 · (samples / 48)) === samples`. Only the electron recorder ingestion path uses
     * this (the legacy listener delivers ms directly), so fractional ms never reaches the legacy WAV
     * branch.
     */
    rtpTimestampToTimestamp(timestamp: number) {
        return (window as Window & { hasOpus?: boolean }).hasOpus || IS_ELECTRON_APP_WITH_RECORDING
            ? // listner OPUS 48000 / (1000/240) = 11520
              timestamp / 48
            : // mock WAV: 16000 / (1000/240) = 3840
              timestamp / 16;
    }

    markAllUnSend() {
        if (this._asrInBackgroundListening) {
            return;
        }

        this._micBuffer.markAllUnSend();
        this._speakerBuffer.markAllUnSend();
    }

    isChunkProcessingAllowed() {
        if (!this.isConnectionOpen() || (!IS_LEGACY_LISTENER_MODE && !IS_ELECTRON_APP_WITH_RECORDING)) {
            return false;
        }

        if (!this.isCallStarted() && !(this._integrationWithKeyphrase && this._asrInBackgroundListening)) {
            return false;
        }

        return true;
    }

    isMicChunkProcessingAllowed() {
        return this._shouldSendMicSound || this._integrationWithKeyphrase;
    }

    isSysChunkProcessingAllowed() {
        return this._shouldSendSystemSound && this._recognitionUseSystem;
    }

    /**
     * The single outgoing-timestamp pipeline for BOTH channels. Turns a buffered chunk's raw stream
     * offset into the value actually sent to Vibe, applying — in this fixed order:
     *   1. zero-base (LEGACY listener path only) — rebase so the call starts at ~0. The anchor is
     *      SHARED across mic+sys (set by whichever channel emits first) so both channels stay on one
     *      timeline; it is keyed to the business callId and survives reconnects. Background listening
     *      is forwarded verbatim — the call has not started yet.
     *   2. clamp — never emit a negative timestamp.
     *   3. emission floor — final per-channel monotonicity backstop (`_enforceVibeEmissionFloor`).
     * (Recorder-clock resets are absorbed earlier, on ingestion, by `_normalizeRecorderChunkTimestamp`.)
     *
     * Electron recorder: normally NO zero-base. The recorder already stamps every chunk with an
     * absolute, sample-accurate, monotonic RTP timestamp (`ts += samples` per Opus packet — exactly
     * Vibe's `next_expected = ts + samples`). Keyphrase opt-in is the one exception: the recorder clock
     * belongs to the background detection session, while the replayed grace buffer starts a NEW business
     * call. In that case we subtract one stable base (the first replayed prefix timestamp) so the saved
     * call timeline starts at 0 and the restarted live recorder continues after the prefix.
     */
    private _prepareOutgoingTimestampMsec(channel: number, rawOffsetMsec: number): number {
        let timestampMsec = rawOffsetMsec;

        if (this._shouldRebaseKeyphraseCallTimestamp()) {
            if (this._keyphraseCallTimestampBaseMsec === null) {
                this._keyphraseCallTimestampBaseMsec = rawOffsetMsec;
            }
            timestampMsec -= this._keyphraseCallTimestampBaseMsec;
        } else if (!this._asrInBackgroundListening && !IS_ELECTRON_APP_WITH_RECORDING) {
            if (channel === CHANNEL.MICROPHONE) {
                if (this._firstMicChunkTimestamp === null && this.isCallStarted()) {
                    this._firstMicChunkTimestamp = this._firstSysChunkTimestamp || rawOffsetMsec;
                }
                timestampMsec -= this._firstMicChunkTimestamp ?? 0;
            } else {
                if (this._firstSysChunkTimestamp === null && this.isCallStarted()) {
                    this._firstSysChunkTimestamp = this._firstMicChunkTimestamp || rawOffsetMsec;
                }
                timestampMsec -= this._firstSysChunkTimestamp ?? 0;
            }
        }

        if (timestampMsec < 0) {
            timestampMsec = 0;
        }

        return this._enforceVibeEmissionFloor(channel, timestampMsec);
    }

    private _shouldRebaseKeyphraseCallTimestamp() {
        return this._integrationWithKeyphrase && !this._asrInBackgroundListening && this.isCallStarted();
    }

    processChunk() {
        if (!this.isChunkProcessingAllowed()) {
            return;
        }

        if (this.isMicChunkProcessingAllowed()) {
            const micChunk = this._micBuffer.mergeToOne();
            if (micChunk && micChunk.audioChunk.length) {
                const micTimestamp = this._prepareOutgoingTimestampMsec(CHANNEL.MICROPHONE, micChunk.offsetInAudioStreamMsec);
                this._micTimestamp = micTimestamp;

                const connection = this._asrConnection;
                if (!connection) {
                    this._captureAudioDiagnosticWarning(
                        'missing_asr_connection_for_mic_chunk',
                        'audio chunk merged but asr connection is missing'
                    );
                    return;
                }

                const sendResult = connection.processChunk({
                    ...micChunk,
                    timestamp: this.timestampToRtpTimestamp(micTimestamp)
                });
                this._trackVibeAudioSend(micTimestamp, micChunk.audioChunk.length, sendResult);
            }
        }

        if (this.isSysChunkProcessingAllowed()) {
            const speakerChunk = this._speakerBuffer.mergeToOne();
            if (speakerChunk && speakerChunk.audioChunk.length) {
                const systemTimestamp = this._prepareOutgoingTimestampMsec(CHANNEL.SYSTEM, speakerChunk.offsetInAudioStreamMsec);
                this._sysTimestamp = systemTimestamp;

                const connection = this._asrConnection;
                if (!connection) {
                    this._captureAudioDiagnosticWarning(
                        'missing_asr_connection_for_system_chunk',
                        'audio chunk merged but asr connection is missing'
                    );
                    return;
                }

                const sendResult = connection.processChunk({
                    ...speakerChunk,
                    timestamp: this.timestampToRtpTimestamp(systemTimestamp)
                });
                this._trackVibeAudioSend(systemTimestamp, speakerChunk.audioChunk.length, sendResult);
            }
        }
    }

    _trackVibeAudioSend(timestampMsec: number, bytes: number, sendResult: unknown) {
        if (sendResult instanceof Error) {
            this._vibeSendFailures += 1;
            this._captureAudioDiagnosticWarning('vibe_audio_send_failed', 'vibe websocket audio send failed', {
                errorMessage: sendResult.message
            });
            return;
        }

        this._vibeChunksSent += 1;
        this._vibeBytesSent += bytes;
        this._lastVibeChunkSentAt = Date.now();
        this._lastVibeChunkTimestampMsec = timestampMsec;
        this._lastVibeChunkBytes = bytes;

        const bufferedAmount =
            typeof sendResult === 'object' &&
            sendResult !== null &&
            typeof (sendResult as { bufferedAmount?: unknown }).bufferedAmount === 'number'
                ? ((sendResult as { bufferedAmount: number }).bufferedAmount as number)
                : typeof (this._asrConnection as { getBufferedAmount?: () => unknown } | null)?.getBufferedAmount === 'function'
                  ? ((this._asrConnection as { getBufferedAmount: () => number }).getBufferedAmount() as number)
                  : 0;

        this._lastVibeBufferedAmount = bufferedAmount;
        if (bufferedAmount > AUDIO_WEBSOCKET_BACKPRESSURE_WARN_BYTES) {
            this._captureAudioDiagnosticWarning('vibe_websocket_backpressure', 'vibe websocket audio send is backpressured', {
                bufferedAmount
            });
        }
    }

    sendPing() {
        if (this.isConnectionOpen() && (IS_LEGACY_LISTENER_MODE || IS_ELECTRON_APP_WITH_RECORDING)) {
            // this._asrConnection.sendPing(); // @todo
        }
    }

    startIamAlive() {
        // Always clear the previous interval first: `startIamAlive` is called from every
        // `connection.on('started', ...)` handler, and a dropped WS / restart would otherwise
        // leak a new interval per reconnect.
        this.stopIamAlive();
        this._iamAliveInterval = setInterval(() => {
            this.sendPing();
        }, 5000);
    }

    stopIamAlive() {
        if (this._iamAliveInterval) {
            clearInterval(this._iamAliveInterval);
            this._iamAliveInterval = null;
        }
    }

    _resetAudioDiagnostics() {
        this._audioDiagnosticWarnings = {};
        this._audioDiagnosticWarningCounts = {};
        this._recorderChunksReceived = 0;
        this._recorderBytesReceived = 0;
        this._recorderChunksDispatched = 0;
        this._recorderChunksDroppedByGate = 0;
        this._recorderDecodeFailures = 0;
        this._vibeChunksSent = 0;
        this._vibeBytesSent = 0;
        this._vibeSendFailures = 0;
        this._lastRecorderChunkReceivedAt = 0;
        this._lastRecorderChunkTimestampMsec = null;
        this._lastRecorderChunkBytes = 0;
        this._lastVibeChunkSentAt = 0;
        this._lastVibeChunkTimestampMsec = null;
        this._lastVibeChunkBytes = 0;
        this._lastVibeBufferedAmount = 0;
    }

    _resetRecorderTimestampContinuity() {
        this._recorderTimestampContinuityByChannel = {};
    }

    /**
     * Rebase ALL Vibe-timestamp tracking for a call: recorder continuity, the per-channel emission
     * floor, and the outgoing zero-base (`_firstMicChunkTimestamp` / `_firstSysChunkTimestamp`).
     *
     * Called only at the boundaries where Vibe's accepted-timestamp history for a callId is empty
     * again: a genuine business-callId change (in `startCall`) or a full `stop`. It is deliberately
     * NOT called per WebSocket connection (`_start`): Vibe correlates a call by the protobuf callId,
     * which is reused across reconnects, so a reset there would let a reconnect's fresh-from-zero
     * stream land below the high-water mark Vibe already holds for that callId. It is likewise NOT
     * called on a re-entrant `startCall` for the same callId, which would re-anchor the zero-base to
     * the current (high) recorder clock and collapse every subsequent frame — the production
     * "timestamp=0 for non-first frame" / "went backward" incident.
     */
    _resetVibeSessionTimestampState() {
        this._resetRecorderTimestampContinuity();
        this._maxEmittedMicTimestampMsec = null;
        this._maxEmittedSysTimestampMsec = null;
        this._lastEmittedMicStepMsec = null;
        this._lastEmittedSysStepMsec = null;
        this._firstMicChunkTimestamp = null;
        this._firstSysChunkTimestamp = null;
        this._vibeTimestampCallId = null;
        this._keyphraseCallTimestampBaseMsec = null;
    }

    /**
     * Per-Vibe-session monotonicity guard at the true send boundary. Returns a timestamp that is
     * strictly greater than anything previously emitted to the current session on this channel.
     *
     * When a non-increasing value would otherwise be sent (a recorder clock reset the continuity
     * layer did not fully absorb, or the legacy zero-timestamp path), it bumps by the DURATION of the
     * previous payload — tracked as the last natural (non-bumped) increment, falling back to a
     * unit-appropriate default only before any step has been observed. Bumping by a real duration
     * (not a fixed minimum) keeps the corrected frame from OVERLAPPING the previous one: Vibe rejects
     * `ts < prev + duration` as "timestamp less than expected", not only true backward jumps, so a
     * fixed sub-duration bump would still be rejected.
     */
    _enforceVibeEmissionFloor(channel: number, timestampMsec: number) {
        const isMic = channel === CHANNEL.MICROPHONE;
        const floor = isMic ? this._maxEmittedMicTimestampMsec : this._maxEmittedSysTimestampMsec;
        const lastStep = isMic ? this._lastEmittedMicStepMsec : this._lastEmittedSysStepMsec;

        let emittedTimestampMsec = timestampMsec;
        if (floor !== null && emittedTimestampMsec <= floor) {
            const step = lastStep && lastStep > 0 ? lastStep : VIBE_EMISSION_MIN_STEP_MSEC;
            emittedTimestampMsec = floor + step;
            this._captureAudioDiagnosticWarning(
                'vibe_emission_floor_enforced',
                'vibe audio timestamp bumped to keep the session monotonic',
                {
                    channel,
                    requestedTimestampMsec: timestampMsec,
                    floorTimestampMsec: floor,
                    emittedTimestampMsec,
                    stepMsec: step
                }
            );
        } else if (floor !== null) {
            // Natural forward step == the duration of this payload. Remember it so a future bump
            // advances by a real audio duration rather than a guessed minimum.
            const naturalStep = emittedTimestampMsec - floor;
            if (naturalStep > 0) {
                if (isMic) {
                    this._lastEmittedMicStepMsec = naturalStep;
                } else {
                    this._lastEmittedSysStepMsec = naturalStep;
                }
            }
        }

        if (isMic) {
            this._maxEmittedMicTimestampMsec = emittedTimestampMsec;
        } else {
            this._maxEmittedSysTimestampMsec = emittedTimestampMsec;
        }

        return emittedTimestampMsec;
    }

    _clearRecorderRestartWatchdog() {
        if (this._recorderRestartWatchdogTimer) {
            clearTimeout(this._recorderRestartWatchdogTimer);
            this._recorderRestartWatchdogTimer = null;
        }
    }

    _resetRecorderRestartRecovery() {
        this._clearRecorderRestartWatchdog();
        this._clearFatalRecorderStopTimer();
        this._recorderRestartAttempts = 0;
        this._recorderRestartRequestedAt = 0;
    }

    _clearFatalRecorderStopTimer() {
        if (this._fatalRecorderStopTimer) {
            clearTimeout(this._fatalRecorderStopTimer);
            this._fatalRecorderStopTimer = null;
        }
    }

    /**
     * Defer a destructive reaction to an unexpected recorder stop (CALL_END / reload) until we can
     * tell whether a live recorder is still producing audio. See RECORDER_STOP_FATAL_GRACE_MSEC: the
     * desktop's single stop channel can't distinguish the tail `close` of a superseded recorder from
     * the current one dying, so we wait one grace window and run `onConfirmed` ONLY if, by then,
     * the recorder is genuinely gone:
     *  - a newer recorder generation has NOT superseded the one this stop was observed under, AND
     *  - no recorder chunk has arrived SINCE the stop (no live recorder is writing).
     * Otherwise the stop belonged to an old/superseded instance and is ignored. Only the most recent
     * pending reaction is kept (a fresh stop reschedules) so we never double-fire.
     */
    _deferRecorderStopReaction(onConfirmed: () => void) {
        const generationAtStop = this._recorderGeneration;
        const chunksAtStop = this._recorderChunksReceived;

        this._clearFatalRecorderStopTimer();
        this._fatalRecorderStopTimer = setTimeout(() => {
            this._fatalRecorderStopTimer = null;

            // A newer recorder start superseded this instance — its own stop events (if any) drive
            // recovery; this stale one must not act against the current recorder.
            if (this._recorderGeneration !== generationAtStop) {
                return;
            }

            // Fresh chunks arrived since the stop → a live recorder is writing, so the stop was the
            // tail of a torn-down instance (restart race). Ignore it.
            if (this._recorderChunksReceived > chunksAtStop) {
                return;
            }

            onConfirmed();
        }, RECORDER_STOP_FATAL_GRACE_MSEC);
    }

    _scheduleRecorderExitedReload() {
        setTimeout(
            () =>
                this._dispatch(
                    reload({
                        reason: 'RECORDER_EXITED',
                        log: false
                    })
                ),
            RECORDER_ERROR_RELOAD_TIMEOUT
        );
    }

    /**
     * Recovery for an unexpectedly-exited recorder (device change / callback starvation), keeping
     * the live Vibe session intact instead of immediately tearing down the whole page. We respawn
     * the recorder in place and continue the timeline from where it stopped — the continuity layer
     * and emission floor absorb the fresh recorder clock. A full RECORDER_EXITED reload remains the
     * fallback if audio never resumes or we exhaust the restart budget.
     */
    _recoverFromUnexpectedRecorderStop() {
        if (!IS_ELECTRON_APP_WITH_RECORDING || !window.electronAPI?.startRecording) {
            this._scheduleRecorderExitedReload();
            return;
        }

        if (this._recorderRestartAttempts >= MAX_RECORDER_RESTART_ATTEMPTS) {
            this._captureAudioDiagnosticWarning(
                'recorder_restart_budget_exhausted',
                'recorder restart budget exhausted, falling back to reload',
                { attempts: this._recorderRestartAttempts }
            );
            this._scheduleRecorderExitedReload();
            return;
        }

        this._recorderRestartAttempts += 1;
        this._recorderRestartRequestedAt = Date.now();

        // Continue from the last forwarded recorder timestamp so the respawned recorder's
        // from-zero clock lands near the session high-water mark, minimising the correction the
        // continuity layer / emission floor have to apply.
        this.startElectronRecording(this._lastRecorderChunkTimestampMsec ?? 0);

        this._clearRecorderRestartWatchdog();
        this._recorderRestartWatchdogTimer = setTimeout(() => {
            this._recorderRestartWatchdogTimer = null;
            if (this._lastRecorderChunkReceivedAt < this._recorderRestartRequestedAt) {
                this._captureAudioDiagnosticWarning(
                    'recorder_restart_recovery_failed',
                    'recorder did not resume after restart, falling back to reload',
                    { attempts: this._recorderRestartAttempts }
                );
                this._dispatch(reload({ reason: 'RECORDER_EXITED', log: false }));
            }
        }, RECORDER_RESTART_RECOVERY_TIMEOUT_MSEC);
    }

    _normalizeRecorderChunkTimestamp(channel: number, rawTimestampMsec: number) {
        const state =
            this._recorderTimestampContinuityByChannel[channel] ||
            (this._recorderTimestampContinuityByChannel[channel] = {
                rawOffsetMsec: 0,
                lastRawTimestampMsec: null,
                lastNormalizedTimestampMsec: null,
                lastStepMsec: null
            });

        if (state.lastRawTimestampMsec !== null) {
            const rawStepMsec = rawTimestampMsec - state.lastRawTimestampMsec;
            if (rawStepMsec > 0) {
                state.lastStepMsec = rawStepMsec;
            }
        }

        let normalizedTimestampMsec = rawTimestampMsec + state.rawOffsetMsec;

        if (state.lastNormalizedTimestampMsec !== null && normalizedTimestampMsec <= state.lastNormalizedTimestampMsec) {
            const stepMsec = state.lastStepMsec && state.lastStepMsec > 0 ? state.lastStepMsec : VIBE_EMISSION_MIN_STEP_MSEC;
            const correctedTimestampMsec = state.lastNormalizedTimestampMsec + stepMsec;
            const previousRawOffsetMsec = state.rawOffsetMsec;

            state.rawOffsetMsec = correctedTimestampMsec - rawTimestampMsec;
            normalizedTimestampMsec = correctedTimestampMsec;

            this._captureAudioDiagnosticWarning(
                'recorder_timestamp_reset_corrected',
                'recorder audio timestamp reset corrected before sending to vibe',
                {
                    channel,
                    rawTimestampMsec,
                    previousRawTimestampMsec: state.lastRawTimestampMsec,
                    previousNormalizedTimestampMsec: state.lastNormalizedTimestampMsec,
                    previousRawOffsetMsec,
                    rawOffsetMsec: state.rawOffsetMsec,
                    stepMsec
                }
            );
        }

        state.lastRawTimestampMsec = rawTimestampMsec;
        state.lastNormalizedTimestampMsec = normalizedTimestampMsec;

        return normalizedTimestampMsec;
    }

    _startAudioDiagnosticsWatchdog() {
        if (this._audioDiagnosticsTimer) {
            clearInterval(this._audioDiagnosticsTimer);
        }

        this._audioDiagnosticsTimer = setInterval(() => {
            this._checkAudioDiagnostics();
        }, AUDIO_DIAGNOSTIC_CHECK_INTERVAL_MSEC);
    }

    _stopAudioDiagnosticsWatchdog() {
        if (!this._audioDiagnosticsTimer) {
            return;
        }

        clearInterval(this._audioDiagnosticsTimer);
        this._audioDiagnosticsTimer = null;
    }

    _audioAge(timestamp: number) {
        return timestamp ? Date.now() - timestamp : null;
    }

    _getAudioDiagnosticsContext(extra: Record<string, unknown> = {}) {
        const recognitionConfig = (this._recognitionConfig || {}) as {
            callId?: unknown;
            sessionInfo?: {
                callId?: unknown;
                callDetectionSessionId?: unknown;
                incallRecognitionSessionNumber?: unknown;
            };
        };

        return {
            source: 'AsrConnectionManager.audioDiagnostics',
            callId: recognitionConfig.callId ?? recognitionConfig.sessionInfo?.callId ?? null,
            callDetectionSessionId: recognitionConfig.sessionInfo?.callDetectionSessionId ?? null,
            incallRecognitionSessionNumber: recognitionConfig.sessionInfo?.incallRecognitionSessionNumber ?? null,
            isCallStarted: this.isCallStarted(),
            isConnectionOpen: this.isConnectionOpen(),
            recorderStartRequested: this._recorderStartRequested,
            callMode: this._callMode,
            feedStarted: Boolean(this._feedStarted),
            recognitionUseSystem: this._recognitionUseSystem,
            shouldSendMicSound: this._shouldSendMicSound,
            shouldSendSystemSound: this._shouldSendSystemSound,
            recorderChunksReceived: this._recorderChunksReceived,
            recorderBytesReceived: this._recorderBytesReceived,
            recorderChunksDispatched: this._recorderChunksDispatched,
            recorderChunksDroppedByGate: this._recorderChunksDroppedByGate,
            recorderDecodeFailures: this._recorderDecodeFailures,
            vibeChunksSent: this._vibeChunksSent,
            vibeBytesSent: this._vibeBytesSent,
            vibeSendFailures: this._vibeSendFailures,
            lastRecorderChunkAgeMsec: this._audioAge(this._lastRecorderChunkReceivedAt),
            lastRecorderChunkTimestampMsec: this._lastRecorderChunkTimestampMsec,
            lastRecorderChunkBytes: this._lastRecorderChunkBytes,
            lastVibeChunkAgeMsec: this._audioAge(this._lastVibeChunkSentAt),
            lastVibeChunkTimestampMsec: this._lastVibeChunkTimestampMsec,
            lastVibeChunkBytes: this._lastVibeChunkBytes,
            lastVibeBufferedAmount: this._lastVibeBufferedAmount,
            callAgeMsec: this._callStartedAt ? Date.now() - this._callStartedAt : null,
            diagnosticCounts: { ...this._audioDiagnosticWarningCounts },
            ...extra
        };
    }

    _captureAudioDiagnosticWarning(kind: string, message: string, extra: Record<string, unknown> = {}) {
        const now = Date.now();
        // Count every occurrence, even the ones we won't log, so the multiplicity survives in the
        // end-of-call diagnostics (e.g. a deep emission-floor staircase that recurs many times).
        this._audioDiagnosticWarningCounts[kind] = (this._audioDiagnosticWarningCounts[kind] || 0) + 1;

        const lastWarningAt = this._audioDiagnosticWarnings[kind] || 0;

        // Throttle per kind: skip only while we are still inside the window since the last LOGGED one.
        if (lastWarningAt && now - lastWarningAt < AUDIO_DIAGNOSTIC_WARN_THROTTLE_MSEC) {
            return;
        }

        this._audioDiagnosticWarnings[kind] = now;
        captureWarnMessage(message, this._getAudioDiagnosticsContext({ diagnosticKind: kind, ...extra }));
    }

    _checkAudioDiagnostics() {
        if (!this.isCallStarted()) {
            return;
        }

        const now = Date.now();
        const callAgeMsec = now - this._callStartedAt;
        if (callAgeMsec < AUDIO_STALL_THRESHOLD_MSEC) {
            return;
        }

        const lastRecorderChunkAgeMsec = this._audioAge(this._lastRecorderChunkReceivedAt);
        if (this._recorderStartRequested && (lastRecorderChunkAgeMsec === null || lastRecorderChunkAgeMsec > AUDIO_STALL_THRESHOLD_MSEC)) {
            this._captureAudioDiagnosticWarning('no_recorder_chunks', 'active call has no recorder audio chunks');
            return;
        }

        if (!this.isChunkProcessingAllowed()) {
            this._captureAudioDiagnosticWarning('chunk_processing_not_allowed', 'active call cannot process recorder audio chunks');
            return;
        }

        if (!this.isMicChunkProcessingAllowed()) {
            this._captureAudioDiagnosticWarning('mic_gate_closed', 'active call mic audio is disabled by coach gate');
            return;
        }

        const lastVibeChunkAgeMsec = this._audioAge(this._lastVibeChunkSentAt);
        if (this._recorderChunksReceived > 0 && (lastVibeChunkAgeMsec === null || lastVibeChunkAgeMsec > AUDIO_STALL_THRESHOLD_MSEC)) {
            this._captureAudioDiagnosticWarning('no_vibe_sends', 'active call has recorder audio but no vibe audio sends');
        }
    }

    _warnFinalAudioGap(sessionDurationMsec: number) {
        if (!sessionDurationMsec || this._lastVibeChunkTimestampMsec === null) {
            return;
        }

        const audioGapMsec = sessionDurationMsec - this._lastVibeChunkTimestampMsec;
        if (audioGapMsec > AUDIO_FINAL_GAP_WARN_THRESHOLD_MSEC) {
            this._captureAudioDiagnosticWarning('final_audio_gap', 'call ended with shorter coach audio stream', {
                sessionDurationMsec,
                audioGapMsec
            });
        }
    }

    resetBuffer(resetInitialTimestamps = true) {
        this._speakerBuffer.reset();
        this._micBuffer.reset();

        if (resetInitialTimestamps) {
            this._firstSysChunkTimestamp = null;
            this._firstMicChunkTimestamp = null;
        }
    }

    flushForwardedEvents() {
        // IMPORTANT: check `connection` BEFORE `shift()`. The previous ordering shifted first and
        // only then bailed on null connection — the shifted event was discarded without being sent
        // and never requeued. Now we only remove the event after we have a connection to send it to.
        while (size(this._forwardedEvents) > 0) {
            const connection = this._asrConnection;
            if (!connection) {
                return;
            }
            const event = this._forwardedEvents.shift();
            connection.sendForwardedEvent(event);
        }

        // Drain the meta that was queued before the connection existed. See `emitCallStartEvent`
        // for why this queue is necessary — meta from telephony/CRM arriving during reconnect was
        // previously lost to Sentry and never made it to the backend, breaking call association.
        while (this._pendingMeta.length > 0) {
            const connection = this._asrConnection;
            if (!connection) {
                return;
            }
            const meta = this._pendingMeta.shift();
            connection.sendMeta(meta as Parameters<typeof connection.sendMeta>[0]);
        }
    }

    forwardEvent(event: unknown) {
        this._forwardedEvents.push(event);
        if (this.isConnectionOpen()) {
            this.flushForwardedEvents();
        }
    }

    utcPing() {
        if (this._explicitPingPeriodMsec > 0 && this._pingCrashTimer) {
            clearTimeout(this._pingCrashTimer);
            this._pingCrashTimer = setTimeout(this.pingCrash.bind(this), this._explicitPingPeriodMsec);
        }
    }

    pingCrash() {
        this._dispatch(utcPingTimeout());
    }

    _markExpectedRecorderStop() {
        if (!this._recorderStartRequested) {
            return;
        }

        this._recorderStartRequested = false;
        this._expectedRecorderStops += 1;

        if (this._expectedRecorderStopTimer) {
            clearTimeout(this._expectedRecorderStopTimer);
        }

        this._expectedRecorderStopTimer = setTimeout(() => {
            this._expectedRecorderStops = 0;
            this._expectedRecorderStopTimer = null;
        }, RECORDER_ERROR_RELOAD_TIMEOUT * 5);
    }

    _consumeExpectedRecorderStop() {
        if (this._expectedRecorderStops <= 0) {
            return false;
        }

        this._expectedRecorderStops -= 1;

        if (this._expectedRecorderStops === 0 && this._expectedRecorderStopTimer) {
            clearTimeout(this._expectedRecorderStopTimer);
            this._expectedRecorderStopTimer = null;
        }

        return true;
    }

    _getRecorderStopDiagnostics(reason: unknown[]) {
        const diagnostics = reason[3];

        return typeof diagnostics === 'object' && diagnostics !== null ? diagnostics : null;
    }

    _handleUnexpectedRecorderByAppStop(reason: unknown[]) {
        const recorderStopDiagnostics = this._getRecorderStopDiagnostics(reason);

        captureWarnMessage('unexpected recorder BY_APP stop during active call', {
            type: 'recorderUnexpectedByAppStop',
            reason,
            recorderStopDiagnostics,
            isCallStarted: this.isCallStarted(),
            integrationWithKeyphrase: this._integrationWithKeyphrase,
            integrationWithRecorderEvents: this._integrationWithRecorderEvents,
            micTimestamp: this._micTimestamp,
            sysTimestamp: this._sysTimestamp
        });

        this._dispatch({
            type: callTypes.CALL_END,
            payload: {
                eventMeta: {
                    source: 'recorder_unexpected_by_app_stop',
                    recorderStopReason: reason[1],
                    recorderStopMessage: reason[2],
                    recorderStopDiagnostics
                }
            }
        });
    }

    /**
     * Voice-energy detection. Decodes a metrics frame (`RecognizeResponse` carrying
     * `resultsMessage.metrics.voiceMetrics`) from the system-audio recorder and feeds it to the
     * voice-activity detector. Returns `true` if it was a metrics frame (handled). Gated by the
     * backend feature flag so nothing is logged when the feature is off.
     */
    private _handleRecorderMetricsChunk(chunk: Uint8Array): boolean {
        if (!this._voiceEnergyDetectionEnabled) {
            return false;
        }

        let voiceMetrics: Record<string, unknown> | undefined;
        try {
            const response = vibeProto.RecognizeResponse.decode(chunk) as unknown;
            // The recorder writes the new MetricsResult location, not the deprecated
            // `resultsMessage.voiceMetrics` field that the Vibe websocket path reads.
            voiceMetrics = (response as { resultsMessage?: { metrics?: { voiceMetrics?: Record<string, unknown> } } })?.resultsMessage
                ?.metrics?.voiceMetrics;
        } catch {
            return false;
        }

        if (!voiceMetrics) {
            return false;
        }

        this._trackAcdVoiceEnergy(voiceMetrics);
        return true;
    }

    /**
     * Voice-energy detection. Adaptive voice-activity detection on the system-audio loudness stream
     * (~1 sample/sec). A fixed loudness threshold does not generalise (idle level varies per machine/
     * device), so we track a noise floor and fire on loudness rising a margin ABOVE that floor, with
     * hysteresis:
     *
     *   - the floor follows the quiet level: it drops instantly to any lower sample, and leaks slowly
     *     upward only while NOT active (so it never chases the speech level and freezes during a turn);
     *   - start when loudness > floor + ONSET_MARGIN; end when it falls back below
     *     floor + RELEASE_MARGIN (RELEASE < ONSET → hysteresis);
     *   - an absolute floor (ACD_VOICE_ENERGY_ABS_SILENCE_LUFS) guards against firing in dead silence;
     *   - a gap timer (`_armAcdSystemSilenceTimer`) ends activity if frames stop arriving (loopback
     *     starves in silence on some machines).
     *
     * Emits exactly two log messages per activity episode (start + end). Logs only; never starts/stops
     * the call.
     */
    private _trackAcdVoiceEnergy(voiceMetrics: Record<string, unknown>) {
        const channel = Number(voiceMetrics.channel ?? 0);
        const loudnessLufs = Number(voiceMetrics.loudnessLufs);
        if (!Number.isFinite(loudnessLufs)) {
            return;
        }

        // Reset the gap watchdog on every SYSTEM frame: if frames then stop arriving while we believe
        // the client is speaking, the timer fires an offset (the loopback starves in silence).
        if (channel === CHANNEL.SYSTEM) {
            this._armAcdSystemSilenceTimer();
        }

        const wasSpeaking = this._acdChannelSpeaking[channel] ?? false;

        // Adaptive noise-floor tracking.
        const prevFloor = this._acdNoiseFloorLufs[channel];
        let floor = prevFloor ?? loudnessLufs;
        if (loudnessLufs < floor) {
            floor = loudnessLufs; // track down to silence instantly
        } else if (!wasSpeaking) {
            // leak upward only when quiet, capped at the current level
            floor = Math.min(loudnessLufs, floor + ACD_NOISE_FLOOR_LEAK_UP_DB);
        }
        this._acdNoiseFloorLufs[channel] = floor;

        const onsetThreshold = floor + ACD_VOICE_ENERGY_ONSET_MARGIN_DB;
        const releaseThreshold = floor + ACD_VOICE_ENERGY_RELEASE_MARGIN_DB;
        const isSpeaking =
            loudnessLufs > ACD_VOICE_ENERGY_ABS_SILENCE_LUFS &&
            (wasSpeaking ? loudnessLufs > releaseThreshold : loudnessLufs > onsetThreshold);

        if (isSpeaking === wasSpeaking) {
            return;
        }

        const round1 = (n: number) => Math.round(n * 10) / 10;
        this._emitAcdEnergyEdge(channel, isSpeaking ? 'onset' : 'offset', {
            reason: 'threshold',
            loudnessLufs: round1(loudnessLufs),
            noiseFloorLufs: round1(floor),
            thresholdLufs: round1(isSpeaking ? onsetThreshold : releaseThreshold),
            measurementTimeMsec: Number(voiceMetrics.measurementTimeMsec ?? 0)
        });
    }

    /**
     * Voice-energy detection. Single place that emits a start/end edge: keeps bookkeeping (last
     * onset/offset) and the log payload consistent whether the edge came from the loudness threshold
     * or from the silence-gap timer. Only logs; never affects the call.
     */
    private _emitAcdEnergyEdge(channel: number, edge: 'onset' | 'offset', fields: Record<string, unknown> = {}) {
        const now = Date.now();
        const isClient = channel === CHANNEL.SYSTEM;
        const isOnset = edge === 'onset';
        this._acdChannelSpeaking[channel] = isOnset;

        const payload: Record<string, unknown> = {
            tag: 'acd_voice_energy',
            edge,
            // CHANNEL.SYSTEM (2) is the customer/client side; CHANNEL.MICROPHONE (1) is the agent.
            channel,
            isClient,
            callActive: this.isCallStarted(),
            sinceMetricsStartMs: this._acdMetricsStartedAt !== null ? now - this._acdMetricsStartedAt : null,
            at: now,
            ...fields
        };

        if (isClient && isOnset) {
            // gapMs = how long the client side was silent before this onset.
            payload.gapMs = this._acdLastSystemOffsetAt !== null ? now - this._acdLastSystemOffsetAt : null;
            this._acdLastClientOnsetAt = now;
        } else if (isClient && !isOnset) {
            if (this._acdLastClientOnsetAt !== null) {
                payload.activityDurationMs = now - this._acdLastClientOnsetAt;
            }
            this._acdLastSystemOffsetAt = now;
            this._clearAcdSystemSilenceTimer();
        }

        // Two log messages per voice-activity episode (start + end), to console and to the backend
        // logger at info level. This is the entire user-facing output of the feature.
        recLog.log(`Voice activity ${isOnset ? 'started' : 'ended'} (system audio)`, payload);
        captureInfoMessage(`acd_voice_energy ${edge}`, payload);
    }

    private _armAcdSystemSilenceTimer() {
        this._clearAcdSystemSilenceTimer();
        this._acdSystemSilenceTimer = setTimeout(() => {
            this._acdSystemSilenceTimer = null;
            // Frames stopped arriving while the client was speaking → the system audio has stopped.
            if (this._acdChannelSpeaking[CHANNEL.SYSTEM]) {
                this._emitAcdEnergyEdge(CHANNEL.SYSTEM, 'offset', { reason: 'metrics_gap' });
            }
        }, ACD_SYSTEM_SILENCE_TIMEOUT_MS);
    }

    private _clearAcdSystemSilenceTimer() {
        if (this._acdSystemSilenceTimer) {
            clearTimeout(this._acdSystemSilenceTimer);
            this._acdSystemSilenceTimer = null;
        }
    }

    startElectronRecording(timestampOffset = 0) {
        // A new recorder instance supersedes any prior one: bump the generation so a deferred
        // stop reaction scheduled for the old instance can recognise it has been superseded.
        this._recorderGeneration += 1;
        this._recorderTimestampOffset = timestampOffset;
        this.stopElectronRecording();
        if (IS_ELECTRON_APP_WITH_RECORDING && window.electronAPI) {
            const extraArgs = [];

            if (this._recognitionUseSystem && !this._asrInBackgroundListening) {
                extraArgs.push('-record-system');
            }

            // NB: the ACD client voice-energy signal is NOT captured by this (microphone) recorder.
            // It runs in a separate, isolated metrics-only recorder process (see
            // `_startSystemMetricsRecording` below) so a system-loopback failure can never destabilise
            // microphone capture / keyphrase detection.

            if (this._recorderSystemAudioDeviceName) {
                extraArgs.push(`-record-system-device-name=${this._recorderSystemAudioDeviceName.normalize('NFC')}`);
            }

            if (this._recorderMicAudioDeviceName) {
                recLog.log(`Recorder mic device name: ${this._recorderMicAudioDeviceName}`);
                extraArgs.push(`-record-mic-device-name=${this._recorderMicAudioDeviceName.normalize('NFC')}`);
            } else if (this._integrationWithKeyphrase) {
                extraArgs.push('-wca-exit-on-default-device-change');
            }

            if (this._integrationWithRecorderEvents && this._appWhitelist && this._appWhitelist.length) {
                extraArgs.push(`-wca-record-exe=${this._appWhitelist.join(',')}`);
            }

            window.electronAPI.startRecording?.({
                recordSysAudio: this._recognitionUseSystem && !this._asrInBackgroundListening, // @deprecated after v0.1.19
                useEvents: this._integrationWithRecorderEvents,
                appWhitelist: this._appWhitelist, // @deprecated after v0.1.19
                extraArgs,
                debug: this._enableRecorderDebugFlag
            });
            this._recorderStartRequested = true;

            // Voice-energy detection is a standalone, backend-gated feature — independent of keyphrase
            // and of background-listening. Whenever it is enabled we run the isolated metrics-only
            // recorder alongside the mic recorder (in any mode/phase) to log voice-activity start/end.
            if (this._voiceEnergyDetectionEnabled) {
                this._startSystemMetricsRecording();
            } else {
                this._stopSystemMetricsRecording();
            }

            if (!this._electronRecorderHooksInited) {
                window.electronAPI.onRecorderChunk((chunk: Uint8Array) => {
                    this._recorderChunksReceived += 1;
                    this._recorderBytesReceived += chunk.length;
                    this._lastRecorderChunkReceivedAt = Date.now();
                    this._lastRecorderChunkBytes = chunk.length;

                    // Audio resumed: a previous unexpected-exit recovery succeeded, so cancel the
                    // reload fallback and restore the full restart budget for any future stall.
                    if (this._recorderRestartAttempts > 0 || this._recorderRestartWatchdogTimer) {
                        this._resetRecorderRestartRecovery();
                    }

                    let audioChunk: ReturnType<typeof vibeProto.AudioChunkWithMetadata.decode>;
                    try {
                        audioChunk = vibeProto.AudioChunkWithMetadata.decode(chunk);
                    } catch (error) {
                        this._recorderDecodeFailures += 1;
                        this._captureAudioDiagnosticWarning('recorder_audio_chunk_decode_failed', 'recorder audio chunk decode failed', {
                            errorMessage: (error as Error).message
                        });
                        return;
                    }

                    // Project the recorder's sample-accurate RTP timestamp to FRACTIONAL ms (the
                    // converter no longer rounds). Keeping the fraction makes the ms→RTP round-trip at
                    // the send boundary exact, eliminating the few-sample overlaps Vibe rejected.
                    const rawChunkTimestamp = this.rtpTimestampToTimestamp(audioChunk.timestamp) + this._recorderTimestampOffset;
                    this._lastRecorderChunkTimestampMsec = rawChunkTimestamp;

                    if (audioChunk.channel === listenerProto.DataFlow.CAPTURE && this.isMicChunkProcessingAllowed()) {
                        const chunkTimestamp = this._normalizeRecorderChunkTimestamp(audioChunk.channel, rawChunkTimestamp);
                        this._lastRecorderChunkTimestampMsec = chunkTimestamp;
                        this._recorderChunksDispatched += 1;
                        this._dispatch(
                            audioChunkReceived({
                                audio: audioChunk.audioChunk,
                                channel: audioChunk.channel,
                                offsetInAudioStreamMsec: chunkTimestamp
                            })
                        );
                    }

                    if (audioChunk.channel === listenerProto.DataFlow.RENDER && this.isSysChunkProcessingAllowed()) {
                        const chunkTimestamp = this._normalizeRecorderChunkTimestamp(audioChunk.channel, rawChunkTimestamp);
                        this._lastRecorderChunkTimestampMsec = chunkTimestamp;
                        this._recorderChunksDispatched += 1;
                        this._dispatch(
                            audioChunkReceived({
                                audio: audioChunk.audioChunk,
                                channel: audioChunk.channel,
                                offsetInAudioStreamMsec: chunkTimestamp
                            })
                        );
                    }

                    const droppedByGate =
                        this.isCallStarted() &&
                        ((audioChunk.channel === listenerProto.DataFlow.CAPTURE && !this.isMicChunkProcessingAllowed()) ||
                            (audioChunk.channel === listenerProto.DataFlow.RENDER && !this.isSysChunkProcessingAllowed()));

                    if (droppedByGate) {
                        this._recorderChunksDroppedByGate += 1;
                        this._captureAudioDiagnosticWarning(
                            'recorder_audio_chunk_dropped_by_gate',
                            'recorder audio chunk dropped by coach gate',
                            {
                                channel: audioChunk.channel,
                                chunkTimestampMsec: rawChunkTimestamp
                            }
                        );
                    } else if (![listenerProto.DataFlow.CAPTURE, listenerProto.DataFlow.RENDER].includes(audioChunk.channel)) {
                        this._captureAudioDiagnosticWarning(
                            'recorder_audio_chunk_unexpected_channel',
                            'recorder audio chunk has unexpected channel',
                            {
                                channel: audioChunk.channel,
                                chunkTimestampMsec: rawChunkTimestamp
                            }
                        );
                    }
                });
                window.electronAPI.onRecorderStopped((reason: unknown[]) => {
                    const recorderStopDiagnostics = this._getRecorderStopDiagnostics(reason);
                    // detect if mic device is not available
                    const msg = `${reason[2]}`;
                    if (
                        msg.includes('unable to determine mic input audio device for recording') ||
                        msg.includes('no default input device')
                    ) {
                        this._recorderStartRequested = false;

                        // Defer: during a stop→respawn race the device-not-found can come from the
                        // OLD instance while a fresh recorder comes up fine. Only tear the call down
                        // if no live recorder is producing audio once the grace window elapses.
                        this._deferRecorderStopReaction(() => {
                            this._dispatch(micDeviceNotAvailable());

                            this._asrConnection?.stop({
                                acdSessionOffsetMsec: 0,
                                asrSessionOffsetMsec: 0,
                                reason: 0,
                                code: wsConnectionClosingCodes.NO_RELOAD
                            });

                            setTimeout(() => {
                                this._dispatch(reload({ reason: 'AUDIO_DEVICE_NOT_FOUND', isImmediate: true, log: false }));
                            }, EXPLICIT_MODE_RELOAD_TIMEOUT);
                        });
                        return;
                    }

                    const expectedRecorderStop = this._consumeExpectedRecorderStop();

                    if (expectedRecorderStop) {
                        return;
                    }

                    this._recorderStartRequested = false;

                    if (reason[1] === 'BY_APP') {
                        if (this.isCallStarted()) {
                            // Defer: a BY_APP stop from a superseded recorder (restart race) must not
                            // end a call whose current recorder is still producing audio. End only if
                            // no live audio is flowing once the grace window elapses.
                            this._deferRecorderStopReaction(() => this._handleUnexpectedRecorderByAppStop(reason));
                        }
                        return;
                    }

                    if (reason[1] !== 'BY_APP') {
                        if (this._integrationWithKeyphrase || this._integrationWithRecorderEvents || this.isCallStarted()) {
                            captureWarnMessage('recorder stopped unexpectedly with desktop diagnostics', {
                                type: 'recorderUnexpectedStop',
                                reason,
                                recorderStopDiagnostics,
                                isCallStarted: this.isCallStarted(),
                                integrationWithKeyphrase: this._integrationWithKeyphrase,
                                integrationWithRecorderEvents: this._integrationWithRecorderEvents,
                                recorderRestartAttempts: this._recorderRestartAttempts,
                                micTimestamp: this._micTimestamp,
                                sysTimestamp: this._sysTimestamp
                            });

                            // Try to respawn the recorder in place (keeping the live Vibe session)
                            // before resorting to a full page reload — desktop updates are hard to
                            // ship, so audio recovery has to happen here in the web app.
                            this._recoverFromUnexpectedRecorderStop();
                        }
                    }
                });

                // Voice-energy detection: metrics frames arrive on their OWN IPC channel from
                // the isolated system-audio recorder, so there is no need to disambiguate them from
                // audio. They never touch the audio pipeline.
                window.electronAPI.onRecorderMetricsChunk?.((chunk: Uint8Array) => {
                    this._handleRecorderMetricsChunk(chunk);
                });

                this._electronRecorderHooksInited = true;
            }
        }
    }

    // Voice-energy detection: spawn the isolated system-audio metrics-only recorder (a second
    // recorder process owned by the desktop app). Best-effort: if the desktop build does not expose
    // the API, this is simply a no-op.
    private _startSystemMetricsRecording() {
        if (IS_ELECTRON_APP_WITH_RECORDING && window.electronAPI?.startSystemMetrics) {
            this._acdChannelSpeaking = {};
            this._acdNoiseFloorLufs = {};
            this._acdMetricsStartedAt = Date.now();
            this._acdLastClientOnsetAt = null;
            this._acdLastSystemOffsetAt = null;
            this._clearAcdSystemSilenceTimer();

            // All recorder flags are built HERE and passed through to the desktop spawner via
            // extraArgs, so we can change them (watchdog, device, future thresholds, …) without
            // rebuilding/redeploying the desktop app. The desktop only adds generic `-verbose -debug`.
            //  - `-record-system -metrics-only`: meter the system output, emit metrics only (no audio).
            //  - `-watchdog=false`: keep metering while the output is silent (WASAPI loopback starves
            //    in silence; the default watchdog would otherwise kill the process before any onset).
            //  - `-record-system-device-name`: meter the SAME configured device that audio recording
            //    uses, not just the default output, when one is configured.
            const extraArgs = ['-record-system', '-metrics-only', '-watchdog=false'];
            if (this._recorderSystemAudioDeviceName) {
                extraArgs.push(`-record-system-device-name=${this._recorderSystemAudioDeviceName.normalize('NFC')}`);
            }

            window.electronAPI.startSystemMetrics({
                debug: this._enableRecorderDebugFlag,
                extraArgs
            });
        }
    }

    private _stopSystemMetricsRecording() {
        this._clearAcdSystemSilenceTimer();
        if (IS_ELECTRON_APP_WITH_RECORDING && window.electronAPI?.stopSystemMetrics) {
            window.electronAPI.stopSystemMetrics();
        }
    }

    stopElectronRecording() {
        if (IS_ELECTRON_APP_WITH_RECORDING && window.electronAPI?.stopRecording) {
            this._markExpectedRecorderStop();
            window.electronAPI.stopRecording();

            this._dispatch(resetSessionDuration());
        }
        this._stopSystemMetricsRecording();
    }

    setEnableRecorderDebugFlag(enableRecorderDebugFlag: boolean) {
        this._enableRecorderDebugFlag = enableRecorderDebugFlag;
    }

    setVoiceEnergyDetectionFlag(enabled: boolean) {
        this._voiceEnergyDetectionEnabled = enabled;
    }
}
